import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import type { UnionAddress } from "@anaconda/core/blockchain/address"
import {
  LISTING_SOURCE_NAME,
  isExternalListing,
} from "@anaconda/core/nft/listing-source"
import type { NftId, NftMiniature } from "@anaconda/core/nft/types"
import type { PriceValue } from "@anaconda/core/price/types"
import { Trans } from "@lingui/react/macro"
import type { CartPaymentSupport } from "@rarible_int/anaconda_sdk/ethereum"
import { action, atom, computed, effect } from "@reatom/core"
import Big from "big.js"
import { molecule } from "bunshi"
import { NftsToBuyScope, type PurchasableNft } from "./scope"
import { getCartPaymentRefusal, isNftAvailableToBuy } from "./utils"

export const NftsToBuyMoleculeReatom = molecule((mol, scope) => {
  const { unionAddress } = mol(WalletConnectMoleculeReatom)
  const initialNftsToBuy = scope(NftsToBuyScope)

  const selectedNftsToBuy = atom(initialNftsToBuy, "selectedNftsToBuy")

  const addNft = action((nft: NftMiniature) => {
    const connectedWallet = unionAddress()
    const nftAvailableToBuy = isNftAvailableToBuy(nft, connectedWallet)

    if (!nftAvailableToBuy) return

    const nftAlreadySelected = selectedNftsToBuy().find(
      ({ id }) => id === nft.id,
    )

    if (nftAlreadySelected) return

    selectedNftsToBuy.set((prev) => [...prev, nft])
  }, "addNft")

  const removeNft = action((nftId: NftId) => {
    selectedNftsToBuy.set((prev) => prev.filter((nft) => nft.id !== nftId))
  }, "removeNft")

  const toggleNftSelection = action((nft: NftMiniature) => {
    const nftAlreadySelected = selectedNftsToBuy().find(
      ({ id }) => id === nft.id,
    )

    if (nftAlreadySelected) {
      removeNft(nft.id)
    } else {
      addNft(nft)
    }
  }, "toggleNftSelection")

  /**
   * Computed that filters out NFTs that are no longer available to be bought
   * This runs reactively whenever selectedNftsToBuy or unionAddress changes
   */
  const selectedNftsToBuyFiltered = computed(() => {
    const connectedWallet = unionAddress()
    const nftsToBuy = selectedNftsToBuy()

    return nftsToBuy.filter((nft) => isNftAvailableToBuy(nft, connectedWallet))
  }, "selectedNftsToBuyFiltered")

  const setNftsToBuy = action((nfts: NftMiniature[]) => {
    const connectedWallet = unionAddress()
    const nftsAvailableToBuy = nfts.filter((nft) => {
      const isAvailable = isNftAvailableToBuy(nft, connectedWallet)

      return isAvailable
    }) as PurchasableNft[]
    // One currency per cart: the first item's.
    const sameCurrency = nftsAvailableToBuy.filter(
      (nft, index) =>
        getCartPaymentRefusal(
          nft,
          nftsAvailableToBuy.slice(0, index),
          undefined,
        ) !== "other-currency",
    )

    selectedNftsToBuy.set(sameCurrency)
  }, "setNftsToBuy")

  const cancelNftSelection = action(() => {
    selectedNftsToBuy.set([])
  }, "cancelNftSelection")

  const selectedNftsTotalPrice = computed(() => {
    const nftsToBuy = selectedNftsToBuy()

    return nftsToBuy.reduce<PriceValue | undefined>((acc, nft) => {
      if (!nft.price) return acc
      if (!acc) return { ...nft.price }

      return Object.assign(acc, {
        amount: Big(acc.amount).plus(nft.price.amount).toString(),
      })
    }, undefined)
  }, "selectedNftsTotalPrice")

  function isNftSelectable(
    nft: NftMiniature,
    userWallet: UnionAddress | undefined,
    /** What the chain's cart contract can be paid in, once known. */
    paymentSupport?: CartPaymentSupport,
  ) {
    if (!nft.price)
      return {
        selectable: false,
        reason: <Trans>Nft is not listed</Trans>,
      } as const

    if (isExternalListing(nft.listingSource)) {
      const sourceName = LISTING_SOURCE_NAME[nft.listingSource]
      return {
        selectable: false,
        reason: <Trans>Listed on {sourceName}</Trans>,
      } as const
    }

    if (userWallet && nft.owner === userWallet)
      return {
        selectable: false,
        reason: <Trans>You already own this NFT</Trans>,
      } as const

    const symbol = nft.price.currency.abbreviation?.toUpperCase()
    switch (getCartPaymentRefusal(nft, selectedNftsToBuy(), paymentSupport)) {
      case "other-currency":
        return {
          selectable: false,
          reason: (
            <Trans>Your cart holds items priced in another currency</Trans>
          ),
        } as const
      case "unsupported-token":
        return {
          selectable: false,
          reason: (
            <Trans>
              The cart can't pay in {symbol}. Buy this one on its own
            </Trans>
          ),
        } as const
      case "opensea-weth":
        return {
          selectable: false,
          reason: (
            <Trans>
              OpenSea listings in {symbol} can't go in the cart yet. Buy this
              one on its own
            </Trans>
          ),
        } as const
    }

    return { selectable: true } as const
  }

  return {
    selectedNftsToBuy,
    selectedNftsToBuyFiltered,
    toggleNftSelection,
    setNftsToBuy,
    removeNft,
    cancelNftSelection,
    selectedNftsTotalPrice,
    isNftSelectable,
  }
})
